/**
 * Dashboard — the v2 app screen.
 *
 * Implements templates/app-screen/AppScreen.dc.html from the "OnFlow Design
 * System" Claude Design project: goal strip, dual TrackRing hero, macro
 * channels, consistency strip, two primary actions. Everything is wired to
 * live data — the template's mock values map onto:
 *
 *   goal strip   → /onboarding profile + /logs/weight (latest) + projection
 *   hero ring    → /logs/nutrition/daily-totals vs the active NutritionTarget,
 *                  inner arc = share of the local day elapsed
 *   macro rows   → same totals vs the target's max values
 *   strip        → /reviews/current adherence.perDay (7 days, not the
 *                  template's 14 — the API exposes one IST week)
 *
 * The legacy stats + recent-activity blocks are kept below the template so
 * nothing that shipped before this redesign is lost.
 */
import type { CSSProperties } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { SkeletonBlock } from '../components/SkeletonBlock.js';
import { ConsistencyStrip } from '../components/v2/ConsistencyStrip.js';
import { FlowButton } from '../components/v2/FlowButton.js';
import { GoalStrip } from '../components/v2/GoalStrip.js';
import { Icon } from '../components/v2/Icon.js';
import { MacroGroup } from '../components/v2/MacroRow.js';
import { TrackRing, TrackRingLegend, trackStateFor } from '../components/v2/TrackRing.js';
import { WeightLogWidget } from '../components/WeightLogWidget.js';
import { useMe } from '../hooks/useAuth.js';
import { useDailyTotalsQuery } from '../hooks/useNutritionLogs.js';
import { useOnboardingProfile } from '../hooks/useOnboarding.js';
import { useCurrentReviewQuery } from '../hooks/useReview.js';
import { useFrequency, useRecentWorkouts, useVolume } from '../hooks/useStats.js';
import { useNutritionTarget, useRecalculateNutritionTarget } from '../hooks/useTargets.js';
import { useWeightLogsQuery } from '../hooks/useWeightLogs.js';
import { formatDuration, formatWeight, muscleLabel, timeAgo } from '../lib/format.js';
import {
  consistencyDays,
  consistencySentence,
  dayElapsedPct,
  daysLeft,
  deadlineRange,
  goalHeadline,
  goalProgressPct,
  greeting,
  localDate,
  projectionState,
  todaysPlan,
} from '../lib/track.js';

const EYEBROW: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'var(--ink-3)',
};

function localTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function Dashboard() {
  const navigate = useNavigate();
  const me = useMe();
  const profile = useOnboardingProfile();
  const targetQuery = useNutritionTarget();
  const recalculateTarget = useRecalculateNutritionTarget();
  const totals = useDailyTotalsQuery({ timezone: localTimezone() });
  const review = useCurrentReviewQuery();
  const weights = useWeightLogsQuery({ pageSize: 1 });
  const volume = useVolume('12w');
  const frequency = useFrequency('12w');
  const recent = useRecentWorkouts(2);

  if (me.isLoading)
    return (
      <div className="empty-state card">
        <p>Loading…</p>
      </div>
    );
  if (!me.data) return null;

  const now = new Date();
  const units = me.data.profile.units;
  const firstName = me.data.user.name?.split(' ')[0] ?? 'there';

  // ── Goal strip ──────────────────────────────────────────────────────────
  const p = profile.data ?? null;
  const modes = p?.trackingModes ?? ['nutrition', 'training'];
  const nutritionEnabled = modes.includes('nutrition');
  const trainingEnabled = modes.includes('training');
  const latestWeightKg = weights.data?.items[0]?.weightKg ?? null;
  const goalState = review.data?.status ?? projectionState(review.data?.projection);
  const goalProgress = goalProgressPct(p?.currentWeightKg, p?.targetWeightKg, latestWeightKg);

  // ── Hero ring + macros ──────────────────────────────────────────────────
  const target = targetQuery.data?.target ?? null;
  const total = totals.data?.total ?? { kcal: 0, proteinG: 0, carbsG: 0, fatG: 0, entryCount: 0 };
  // The ring reads against the midpoint of the target band — a single number
  // the user can aim at, while the macro rows below keep the band's max.
  const kcalTarget = target ? Math.round((target.calorieMin + target.calorieMax) / 2) : 0;
  const intakePct = kcalTarget > 0 ? (total.kcal / kcalTarget) * 100 : 0;
  const dayPct = dayElapsedPct(now);

  // ── Consistency strip ───────────────────────────────────────────────────
  const content = review.data?.review.content;
  const perDay = content?.schemaVersion === 'v2' ? content.adherence.perDay : undefined;
  const days = consistencyDays(perDay, localDate(now));
  const stripSentence = consistencySentence(days);

  // ── Today's plan ────────────────────────────────────────────────────────
  const thisWeek = thisWeekCount(frequency.data?.points);
  const weeklyGoal = frequency.data?.weeklyGoal ?? me.data.profile.weeklyGoal;
  const totalWorkouts = volume.data?.points.reduce((s, pt) => s + (pt.workouts ?? 0), 0) ?? 0;
  const recentWorkouts = recent.data?.items ?? [];
  const todayIso = localDate(now);
  const planItems = todaysPlan({
    proteinG: total.proteinG,
    proteinTargetG: target?.proteinMinG ?? 0,
    trainedToday: recentWorkouts.some((w) => localDate(new Date(w.date)) === todayIso),
    weighedToday:
      weights.data?.items[0] != null &&
      localDate(new Date(weights.data.items[0].loggedAt)) === todayIso,
    latestWeightKg,
    weeklyGoal,
    sessionsThisWeek: thisWeek,
  }).filter(
    (item) =>
      item.to === '/profile' ||
      (nutritionEnabled && item.to === '/log') ||
      (trainingEnabled && item.to === '/workout/new'),
  );

  return (
    <section className="fade-in">
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          padding: '6px 0 16px',
        }}
      >
        <div>
          <span style={{ ...EYEBROW, display: 'block', marginBottom: 6 }}>
            {now.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
          </span>
          <h1
            style={{
              margin: 0,
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-title-size)',
              lineHeight: 'var(--text-title-line)',
              letterSpacing: 'var(--text-title-tracking)',
              fontWeight: 700,
              color: 'var(--ink-1)',
            }}
          >
            {greeting(now)}, {firstName}
          </h1>
        </div>
        <WeightLogWidget hideWhenLoggedToday />
      </header>

      <Link to="/profile" style={{ display: 'block', textDecoration: 'none' }}>
        <GoalStrip
          compact
          goal={goalHeadline(p)}
          deadline={deadlineRange(p?.createdAt ?? null, p?.targetDate ?? null)}
          daysLeft={daysLeft(p?.targetDate ?? null, now)}
          state={goalState}
          progress={goalProgress}
          style={{ marginBottom: 14 }}
        />
      </Link>

      {nutritionEnabled && (
        <div
          data-testid="tracker-hero"
          style={{
            background: 'var(--paper-1)',
            border: '1px solid var(--edge)',
            borderRadius: 'var(--r-lg)',
            padding: 22,
            boxShadow: 'var(--elev-2)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <TrackRing
            progress={intakePct}
            elapsed={dayPct}
            size={196}
            label="Eaten today"
            value={Math.round(total.kcal).toLocaleString()}
            suffix={kcalTarget > 0 ? `of ${kcalTarget.toLocaleString()} kcal` : 'kcal logged'}
          />
          <TrackRingLegend
            state={trackStateFor(intakePct, dayPct)}
            progressLabel="Today's intake"
            elapsedLabel="Day elapsed"
          />
          <div style={{ width: '100%', height: 1, background: 'var(--edge)' }} />
          <MacroGroup
            style={{ width: '100%' }}
            protein={{ current: total.proteinG, target: target?.proteinMaxG ?? 0 }}
            carbs={{ current: total.carbsG, target: target?.carbsMaxG ?? 0 }}
            fat={{ current: total.fatG, target: target?.fatMaxG ?? 0 }}
          />
          <div
            data-testid="nutrition-target-card"
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
            }}
          >
            <span style={{ ...EYEBROW, fontVariantNumeric: 'tabular-nums' }}>
              {target
                ? `Target ${target.calorieMin}–${target.calorieMax} kcal · ${target.proteinMinG}–${target.proteinMaxG} g P`
                : 'No target set'}
            </span>
            <FlowButton
              variant="quiet"
              size="sm"
              disabled={recalculateTarget.isPending}
              onClick={() => recalculateTarget.mutate()}
            >
              {recalculateTarget.isPending ? 'Recalculating…' : 'Recalculate'}
            </FlowButton>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
        {nutritionEnabled && (
          <FlowButton variant="primary" size="lg" block onClick={() => navigate('/log')}>
            Log food
          </FlowButton>
        )}
        {trainingEnabled && (
          <FlowButton variant="soft" size="lg" block onClick={() => navigate('/workout/new')}>
            Train
          </FlowButton>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          margin: '22px 0 10px',
        }}
      >
        <span style={EYEBROW}>Today&apos;s plan</span>
        <span style={{ ...EYEBROW, letterSpacing: '0.06em', color: 'var(--ink-2)' }}>
          {planItems.filter((i) => i.done).length} of {planItems.length} done
        </span>
      </div>
      <div
        style={{
          background: 'var(--paper-1)',
          border: '1px solid var(--edge)',
          borderRadius: 'var(--r-md)',
          boxShadow: 'var(--elev-1)',
          overflow: 'hidden',
        }}
      >
        {planItems.map((item, i) => (
          <Link
            key={item.title}
            to={item.to}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              width: '100%',
              padding: '13px 14px',
              borderTop: i === 0 ? undefined : '1px solid var(--edge)',
              textDecoration: 'none',
            }}
          >
            <span
              style={{
                width: 26,
                height: 26,
                flexShrink: 0,
                borderRadius: 'var(--r-full)',
                display: 'grid',
                placeItems: 'center',
                border: '1px solid var(--edge)',
                background: item.done ? 'var(--on-track)' : 'var(--paper-3)',
                color: item.done ? 'var(--paper-0)' : 'var(--paper-3)',
                boxShadow: item.done
                  ? '0 0 16px color-mix(in srgb, var(--on-track) 32%, transparent)'
                  : 'none',
                animation: item.done ? 'v2-pop 260ms var(--ease-snap)' : undefined,
              }}
            >
              <Icon name="check" size={15} strokeWidth={2.6} />
            </span>
            <span style={{ minWidth: 0, flex: 1 }}>
              <span
                style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 600,
                  color: item.done ? 'var(--ink-3)' : 'var(--ink-1)',
                  textDecoration: item.done ? 'line-through' : 'none',
                }}
              >
                {item.title}
              </span>
              <span
                style={{
                  display: 'block',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  color: 'var(--ink-3)',
                  marginTop: 3,
                }}
              >
                {item.meta}
              </span>
            </span>
          </Link>
        ))}
      </div>

      {nutritionEnabled && (
        <>
          <div style={{ ...EYEBROW, margin: '22px 0 10px' }}>Last 7 days</div>
          <div
            style={{
              background: 'var(--paper-1)',
              border: '1px solid var(--edge)',
              borderRadius: 'var(--r-md)',
              padding: 18,
              boxShadow: 'var(--elev-1)',
            }}
          >
            {days.length > 0 ? (
              <ConsistencyStrip days={days} height={62} />
            ) : (
              <SkeletonBlock width="100%" height={62} />
            )}
            <p
              style={{
                margin: '14px 0 0',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--text-body-sm-size)',
                lineHeight: 'var(--text-body-sm-line)',
                color: 'var(--ink-2)',
              }}
            >
              {stripSentence}
            </p>
          </div>
        </>
      )}

      {trainingEnabled && (
        <>
          <div className="dashboard-stats" style={{ marginTop: 22 }}>
            <div className="stat-card">
              <div className="stat-label">Total Workouts</div>
              {volume.isLoading && !volume.data ? (
                <SkeletonBlock width={64} height={28} />
              ) : (
                <div className="stat-value">{totalWorkouts}</div>
              )}
            </div>
            <div className="stat-card">
              <div className="stat-label">This Week</div>
              {frequency.isLoading && !frequency.data ? (
                <SkeletonBlock width={64} height={28} />
              ) : (
                <div className="stat-value">
                  {thisWeek}
                  <span className="stat-unit">/{weeklyGoal}</span>
                </div>
              )}
            </div>
          </div>

          <section className="dashboard-section">
            <header className="dashboard-section-head">
              <h2 className="section-title">Recent Activity</h2>
              {recentWorkouts.length > 0 && (
                <Link to="/history" className="btn-link">
                  See All
                </Link>
              )}
            </header>

            {recent.isLoading && !recent.data ? (
              <>
                {[0, 1].map((i) => (
                  <div key={i} className="workout-card" aria-hidden>
                    <div className="workout-card-header">
                      <div style={{ flex: 1 }}>
                        <SkeletonBlock width="50%" height={18} />
                        <div style={{ marginTop: 8 }}>
                          <SkeletonBlock width="30%" height={12} />
                        </div>
                      </div>
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <SkeletonBlock width="70%" height={14} />
                    </div>
                  </div>
                ))}
              </>
            ) : recentWorkouts.length === 0 ? (
              <div className="empty-state card">
                <p>No workouts yet. Start tracking your progress today.</p>
              </div>
            ) : (
              recentWorkouts.map((w) => (
                <Link
                  key={w.id}
                  to={`/history/${w.id}`}
                  className="workout-card workout-card-link"
                  aria-label={`View workout: ${w.title || 'Workout'}`}
                >
                  <div className="workout-card-header">
                    <div>
                      <div className="workout-card-title">{w.title || 'Workout'}</div>
                      <div className="workout-card-date">{timeAgo(w.date)}</div>
                    </div>
                  </div>
                  <div className="workout-card-stats">
                    <span>{formatDuration(w.duration)}</span>
                    <span>{formatWeight(w.totalVolume, units)}</span>
                    <span>{w.totalSets} sets</span>
                  </div>
                  {(w.muscleGroups ?? []).length > 0 && (
                    <div className="workout-card-tags">
                      {w.muscleGroups.map((m) => (
                        <span key={m} className={`tag tag-${m}`}>
                          {muscleLabel(m)}
                        </span>
                      ))}
                    </div>
                  )}
                </Link>
              ))
            )}
          </section>
        </>
      )}
    </section>
  );
}

function thisWeekCount(points: { weekStart: string; workouts: number }[] | undefined): number {
  if (!points || points.length === 0) return 0;
  return points[points.length - 1]?.workouts ?? 0;
}
